import API from './api';
import { CustomerSensitiveData } from './auth';

export interface ApplicationPayload {
  form: any;
}

export interface Application {
  id: number;
  unit_id: number;
  building_id: number;
  form: any;
  status: ApplicationStatus;
  lease_id: number | null;
  fee_charge_id: number | null;
  customer_id: number;
  approved_background_check_id: number | null;
  transunion_screening_request_renter_id: number | null;
  transunion_screening_request_id: number | null;
  transunion_screening_request_bundle_id: number | null;
  transunion_exam_id: number | null;
  transunion_requires_manual_verification: boolean | null;
  identity_verification_expiration_date: Date | null;
}

export interface BackgroundCheck {
  id: number;
}

export enum ApplicationStatus {
  backgroundCheckFailed = 'background_check_failed',
  pending = 'pending',
  submitted = 'submitted',
}

export const createApplication = async (
  unitId: number
): Promise<Application> => {
  const response = await API.post(
    `${API.BASE_URL}/leasing/units/${unitId}/applications/`
  );
  return response;
};

export const getApplications = (unitId: number): Promise<Application[]> =>
  API.get(`${API.BASE_URL}/leasing/units/${unitId}/applications`);

export const updateApplication = (
  unitId: number,
  applicationId: number,
  payload: ApplicationPayload
): Promise<Application> =>
  API.put(
    `${API.BASE_URL}/leasing/units/${unitId}/applications/${applicationId}`,
    {
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

export const payApplicationFee = async (
  unitId: number,
  applicationId: number,
  tokenId: string
): Promise<Application> => {
  return API.post(
    `${API.BASE_URL}/leasing/units/${unitId}/applications/${applicationId}/pay/${tokenId}`
  );
};

export const postIdentityVerification = ({
  sensitiveData,
  unitId,
  applicationId,
}: {
  sensitiveData: CustomerSensitiveData;
  unitId: number;
  applicationId: number;
}): Promise<{ verified: boolean }> => {
  return API.post(
    `${API.BASE_URL}/leasing/units/${unitId}/applications/${applicationId}/identity-verification`,
    {
      body: JSON.stringify({ ...sensitiveData }),
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );
};

export const postIdentityVerificationExamAnswers = ({
  answers,
  unitId,
  applicationId,
}: {
  unitId: number;
  applicationId: number;
  answers: ExamAnswer[];
}): Promise<PostIdentityExamAnswersResponse> => {
  return API.post(
    `${API.BASE_URL}/leasing/units/${unitId}/applications/${applicationId}/identity-verification/exam/answers`,
    {
      body: JSON.stringify({ answers }),
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );
};

export const getIdentityVerificationExam = ({
  sensitiveData,
  unitId,
  applicationId,
}: {
  sensitiveData: CustomerSensitiveData;
  unitId: number;
  applicationId: number;
}): Promise<GetIdentityExamResponse> => {
  return API.post(
    `${API.BASE_URL}/leasing/units/${unitId}/applications/${applicationId}/identity-verification/exam`,
    {
      body: JSON.stringify({ ...sensitiveData }),
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );
};

export const getBackgroundCheckStatus = (
  unitId: number,
  applicationId: number
): Promise<{ progress: BackgroundCheckProgress }> =>
  API.get(
    `${API.BASE_URL}/leasing/units/${unitId}/applications/${applicationId}/background-check/progress`
  );

export const getBackgroundCheckResult = (
  unitId: number,
  applicationId: number
): Promise<BackgroundCheck> =>
  API.get(
    `${API.BASE_URL}/leasing/units/${unitId}/applications/${applicationId}/background-check/result`
  );

export const triggerBackgroundCheck = (
  unitId: number,
  applicationId: number,
  optIn: boolean
): Promise<BackgroundCheckCreationResponse> =>
  API.post(
    `${API.BASE_URL}/leasing/units/${unitId}/applications/${applicationId}/background-check`,
    {
      body: JSON.stringify({ optInToShare: optIn }),
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

export const createIncomeVerificationRequest = (
  unitId: number,
  applicationId: number,
  plaidTokens: string[]
): Promise<void> =>
  API.post(
    `${API.BASE_URL}/leasing/units/${unitId}/applications/${applicationId}/income-verification`,
    {
      body: JSON.stringify({ plaid_tokens: plaidTokens }),
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

export const getIncomeVerificationStatus = (
  unitId: number,
  applicationId: number
): Promise<IncomeVerificationReport> => {
  return API.get(
    `${API.BASE_URL}/leasing/units/${unitId}/applications/${applicationId}/income-verification/report`
  );
};

export const submitApplication = (
  unitId: number,
  applicationId: number
): Promise<void> =>
  API.post(
    `${API.BASE_URL}/leasing/units/${unitId}/applications/${applicationId}/submit`
  );

export const requestTransUnionReport = (
  unitId: number,
  applicationId: number,
  payload: CustomerSensitiveData
): Promise<void> =>
  API.post(
    `${API.BASE_URL}/leasing/units/${unitId}/applications/${applicationId}/transunion/report`,
    {
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

export const getCurrentCustomerLease = (
  customerId: string
): Promise<CurrentCustomerLease> =>
  API.get(`${API.BASE_URL}leasing/users/${customerId}/current-lease`);

export const getCurrentCustomer = (
  customerId: string
): Promise<CurrentCustomer> => {
  return API.get(`${API.BASE_URL}leasing/users/${customerId}/current-customer`);
};

export const getReportsForApplication = (
  unitId: string | number,
  applicationId: string | number
): Promise<TransUnionReportsResponse> => {
  return API.get(
    `${API.BASE_URL}leasing/units/${unitId}/applications/${applicationId}/transunion-reports-documents`
  );
};

export const getReportDocument = (
  unitId: string | number,
  applicationId: string | number,
  documentId: string | number
): Promise<ReportsResponse> => {
  return API.get(
    `${API.BASE_URL}leasing/units/${unitId}/applications/${applicationId}/transunion-reports-documents/${documentId}`
  );
};

export interface TransUnionReportsDocument {
  id: number;
  lease_application_id: number;
  building_document_id: number;
  type: string;
  status: string;
  expires_at: Date;
}
export interface TransUnionReportsResponse {
  reports: TransUnionReportsDocument[];
}

export interface ReportsResponse {
  reportsExpireNumberOfDays: number;
  reportResponseModelDetails: Array<{
    providerName: string;
    reportData: string;
  }>;
}

export interface IncomeVerificationReport {
  annualIncome: string;
  avgMonthlyIncome: string;
  employer?: string;
  incomeToRentRatio?: string;
  totalAssets: string;
  numberOfAccounts?: string;
}

export type IncomeVerificationResponse =
  | IncomeVerificationFailedResponse
  | IncomeVerificationReport;

export interface IncomeVerificationFailedResponse {
  annualIncome: string;
}

export const INCOME_NOT_FOUND =
  'Income information not found for provided accounts. Please have the property manager and resident coordinate directly to verify their self-reported income.';

export interface BackgroundCheckCreationResponse {
  webviewContentUrl: string;
}

export enum BackgroundCheckProgress {
  NotStarted = 'not_started',
  UserActionRequired = 'user_action_required',
  InProgress = 'in_progress',
  Denied = 'denied',
  Approved = 'approved',
}

export interface LeaseData {
  lease_end: Date;
  lease_start: Date;
  move_in: Date;
  move_out: Date | null;
  rent: number;
  unit_id: number;
}

export interface CustomerLocation {
  address1: string;
  city: string;
  formatted_address?: string;
  geo?: { lat: number; lng: number };
  place_id?: string;
  postal_code: string;
  state: string;
  unit: string;
}

export interface CurrentCustomerLease {
  id: number;
  unit_id: number;
  lease_start: Date;
  lease_end: Date;
  move_in: Date;
  move_out: Date;
  rent: number;
  location: CustomerLocation;
  building_id: number;
  can_renew_lease: boolean;
}

export interface CurrentCustomer {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  location: CustomerLocation;
  dob?: Date;
}

export interface ExamAnswer {
  questionKeyName: string;
  selectedChoiceKeyName: string;
}
export interface ExamResponse {
  examId: number;
  authenticationQuestions: ExamQuestion[];
  result: string;
  externalReferenceNumber: string;
}

export interface ExamQuestion {
  questionKeyName: string;
  questionDisplayName: string;
  type: string;
  choices: ExamQuestionChoice[];
}

export interface ExamQuestionChoice {
  choiceKeyName: string;
  choiceDisplayName: string;
}
export interface GetIdentityExamResponse {
  requires_manual_verification: boolean;
  quiz: ExamResponse;
}

export interface PostIdentityExamAnswersResponse {
  exam_status: ExamStatus;
  requires_manual_verification: boolean;
}

export enum ExamStatus {
  PASSED = 'passed',
  FAILED = 'failed',
  REQUIRES_MANUAL_VERIFICATION = 'requires_manual_verification',
  PENDING = 'pending',
}
